import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Product, CreateProductData, productService } from '../../services/productService';

import CustomCombobox from '../common/Combobox';
import toast from 'react-hot-toast';


interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    productToEdit?: Product | null;
    initialInventoryType?: 'maestro' | 'novias';
}

type Tab = 'individual' | 'multiple';

interface BulkRow {
    id: string;
    cod_producto: string;
    marca: string;
    color: string;
    talla: string;
    costo: number; // Not used in API yet but good for future? Or maybe mapped to something? Frontend uses it?
    // Actually API CreateProductData has precio, but maybe costo_prod?
    // Let's stick to CreateProductData fields.
    precio: number;
    stock: number;
    codigo_proveedor?: string;
    type?: 'maestro' | 'novias';
}

export const ProductEntryModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onSuccess,
    productToEdit,
    initialInventoryType = 'maestro'
}) => {
    const [activeTab, setActiveTab] = useState<Tab>('individual');
    const [loading, setLoading] = useState(false);

    // --- Individual State ---
    const [indForm, setIndForm] = useState<CreateProductData>({
        cod_producto: '',
        color: '',
        marca: '',
        precio: 0,
        costo: undefined,
        talla: '',
        stock: 0,
        activo: true,
        codigo_proveedor: '',
        type: initialInventoryType
    });

    // --- Bulk State ---
    const [bulkRows, setBulkRows] = useState<BulkRow[]>([
        { id: '1', cod_producto: '', marca: '', color: '', talla: '', costo: 0, precio: 0, stock: 1 }
    ]);

    // --- Common Options ---
    const [existingBrands, setExistingBrands] = useState<string[]>([]);
    const [existingCodes, setExistingCodes] = useState<string[]>([]);
    const [brandColors, setBrandColors] = useState<{ brand: string; color: string }[]>([]);
    const [brandSizes, setBrandSizes] = useState<{ brand: string; size: string }[]>([]); // Added state
    const [codeBrands, setCodeBrands] = useState<{ code: string; brand: string }[]>([]);

    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const [availableSizes, setAvailableSizes] = useState<string[]>([]);

    // Load filter options
    useEffect(() => {
        if (isOpen) {
            loadOptions();
            if (productToEdit) {
                setIndForm({
                    cod_producto: productToEdit.cod_producto || '',
                    color: productToEdit.color || '',
                    marca: productToEdit.marca || '',
                    precio: productToEdit.precio || 0,
                    costo: productToEdit.costo || undefined,
                    talla: productToEdit.talla || '',
                    stock: productToEdit.stock != null ? productToEdit.stock : 1,
                    activo: productToEdit.activo != null ? productToEdit.activo : true,
                    codigo_proveedor: productToEdit.codigo_proveedor || '',
                    type: productToEdit.inventoryType || initialInventoryType
                });
                setActiveTab('individual');
            } else {
                setIndForm({
                    cod_producto: '',
                    color: '',
                    marca: '',
                    precio: 0,
                    costo: undefined,
                    talla: '',
                    stock: 1,
                    activo: true,
                    codigo_proveedor: '',
                    type: initialInventoryType
                });
            }
        }
    }, [isOpen, productToEdit, initialInventoryType]);

    // Load dynamic variants when code changes
    useEffect(() => {
        if (indForm.cod_producto) {
            productService.getVariants(indForm.cod_producto, indForm.type as 'maestro' | 'novias')
                .then(res => {
                    setAvailableColors(res.colors);
                    setAvailableSizes(res.sizes);
                })
                .catch(() => {
                    setAvailableColors([]);
                    setAvailableSizes([]);
                });
        } else {
            setAvailableColors([]);
            setAvailableSizes([]);
        }
    }, [indForm.cod_producto, indForm.type]);

    // ...

    const loadOptions = async () => {
        try {
            const options = await productService.getFilterOptions();

            const m = options.maestro || {} as any;
            const n = options.novias || {} as any;

            const allMarcas = Array.from(new Set([...(m.marcas || []), ...(n.marcas || []), ...(options.marcas || [])]));
            const allCodes = Array.from(new Set([...(m.codes || []), ...(n.codes || [])]));
            const allBrandColors = [...(m.brandColors || []), ...(n.brandColors || [])];
            const allBrandSizes = [...(m.brandSizes || []), ...(n.brandSizes || [])]; // Fetch sizes
            const allCodeBrands = [...(m.codeBrands || []), ...(n.codeBrands || [])];

            setExistingBrands(allMarcas);
            setExistingCodes(allCodes);
            setBrandColors(allBrandColors);
            setBrandSizes(allBrandSizes); // Set sizes
            setCodeBrands(allCodeBrands);
        } catch (e) {
            console.error("Error loading options", e);
        }
    };

    // --- Individual Logic ---
    const handleIndSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (productToEdit) {
                await productService.updateProduct(productToEdit.id_producto, indForm);
                toast.success('Producto actualizado');
            } else {
                await productService.createProduct({
                    ...indForm,
                    type: indForm.type || initialInventoryType
                });
                toast.success('Producto creado');
            }
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    // Filter colors by brand for Individual
    const getColorsForBrand = (brand: string) => {
        if (!brand) return [];
        return Array.from(new Set(brandColors.filter(bc => bc.brand === brand).map(bc => bc.color)));
    };

    const getSizesForBrand = (brand: string) => {
        if (!brand) return [];
        return Array.from(new Set(brandSizes.filter(bs => bs.brand === brand).map(bs => bs.size)));
    };

    // --- Bulk Logic ---
    const addRow = () => {
        setBulkRows([
            ...bulkRows,
            { id: Date.now().toString(), cod_producto: '', marca: '', color: '', talla: '', costo: 0, precio: 0, stock: 1 }
        ]);
    };

    const removeRow = (id: string) => {
        if (bulkRows.length > 1) {
            setBulkRows(bulkRows.filter(r => r.id !== id));
        }
    };

    const updateRow = (id: string, field: keyof BulkRow, value: any) => {
        setBulkRows(bulkRows.map(r => {
            if (r.id === id) {
                const updated = { ...r, [field]: value };
                // Logic sync: clear color if brand changes?
                if (field === 'marca') {
                    // Maybe keep color if valid, but usually reset?
                    // Let's keep it simple for now as requested "same logic as invoice"
                }
                return updated;
            }
            return r;
        }));
    };

    const handleBulkSubmit = async () => {
        // Validate
        const invalid = bulkRows.some(r => !r.marca || !r.cod_producto);
        if (invalid) {
            toast.error("Marca y Código son obligatorios en todas las filas");
            return;
        }

        if (!window.confirm(`¿Estás seguro de agregar ${bulkRows.length} productos al inventario?`)) return;

        setLoading(true);
        try {
            // Process individually (parallel or sequential)
            // Or use a bulk endpoint if available? 
            // The prompt says "NO generated shipmentId".
            // We can reuse createProduct loop.

            const promises = bulkRows.map(row => productService.createProduct({
                cod_producto: row.cod_producto,
                marca: row.marca,
                color: row.color,
                talla: row.talla,
                precio: row.precio,
                costo: row.costo || undefined,
                stock: row.stock,
                codigo_proveedor: row.codigo_proveedor, // Auto-lookup in backend
                activo: true,
                type: row.type || initialInventoryType
            }));

            await Promise.all(promises);
            toast.success(`${bulkRows.length} productos agregados`);
            onSuccess();
            onClose();
        } catch (e) {
            console.error(e);
            toast.error("Error al procesar algunos productos");
        } finally {
            setLoading(false);
        }
    };


    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[1000]" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl max-h-[90vh] flex flex-col animate-fadeIn" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="px-6 pt-6 pb-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-t-2xl flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
                            {productToEdit ? 'Editar Producto' : 'Agregar Productos'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {productToEdit
                                ? 'Modifica los detalles del producto existente'
                                : 'Registra nuevos productos en el inventario'}
                        </p>
                    </div>

                    {/* Tabs (Only if not editing) */}
                    {!productToEdit && (
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setActiveTab('individual')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'individual' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Individual
                            </button>
                            <button
                                onClick={() => setActiveTab('multiple')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'multiple' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Carga Múltiple
                            </button>
                        </div>
                    )}

                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {activeTab === 'individual' ? (
                        /* --- INDIVIDUAL FORM --- */
                        <form onSubmit={handleIndSubmit} className="max-w-3xl mx-auto space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Marca (Proveedor) *
                                    </label>
                                    {/* Brand Selection - Unlocked so they can fix it manually if needed */}
                                    <CustomCombobox
                                        value={indForm.marca || ''}
                                        onChange={(val) => setIndForm({ ...indForm, marca: val, color: '' })} // Reset color on brand change
                                        options={existingBrands}
                                        placeholder="Selecciona Marca"
                                        className="w-full"
                                        allowCustom={false} // Strict
                                        disabled={false}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Código de Producto *
                                    </label>
                                    <CustomCombobox
                                        value={indForm.cod_producto}
                                        onChange={(val) => {
                                            const found = codeBrands.find(cb => cb.code.toLowerCase() === val.toLowerCase());
                                            setIndForm({
                                                ...indForm,
                                                cod_producto: val,
                                                marca: found ? found.brand : indForm.marca
                                            });
                                        }}
                                        options={existingCodes}
                                        placeholder="Ej: 5630"
                                        className="w-full"
                                        allowCustom={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Color
                                    </label>
                                    <CustomCombobox
                                        value={indForm.color || ''}
                                        onChange={(val) => setIndForm({ ...indForm, color: val })}
                                        options={availableColors.length > 0 ? Array.from(new Set(availableColors)) : getColorsForBrand(indForm.marca || '')}
                                        placeholder="Ej: NEGRO"
                                        className="w-full"
                                        allowCustom={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Talla
                                    </label>
                                    <CustomCombobox
                                        value={indForm.talla || ''}
                                        onChange={(val) => setIndForm({ ...indForm, talla: val })}
                                        options={availableSizes.length > 0 ? Array.from(new Set(availableSizes)) : getSizesForBrand(indForm.marca || '')}
                                        placeholder="Ej: M"
                                        className="w-full"
                                        allowCustom={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Precio Público
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={indForm.precio}
                                        onChange={(e) => setIndForm({ ...indForm, precio: parseFloat(e.target.value) })}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-purple-700 mb-1">
                                        Costo Proveedor USD
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={indForm.costo || ''}
                                        onChange={(e) => setIndForm({ ...indForm, costo: e.target.value ? parseFloat(e.target.value) : undefined })}
                                        placeholder="Ej: 25.00"
                                        className="w-full px-3 py-2 border border-purple-200 bg-purple-50 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Opcional. Para calcular precio Mayoreo.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Stock Actual
                                    </label>
                                    <input
                                        type="number"
                                        value={indForm.stock}
                                        onChange={(e) => setIndForm({ ...indForm, stock: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                                    />
                                </div>



                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Inventario
                                    </label>
                                    <CustomCombobox
                                        value={indForm.type === 'novias' ? 'Novias' : 'General'}
                                        onChange={(val) => setIndForm({ ...indForm, type: val === 'Novias' ? 'novias' : 'maestro' })}
                                        options={['General', 'Novias']}
                                        placeholder="Seleccionar..."
                                        allowCustom={false}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 mr-3"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                                >
                                    {loading ? 'Guardando...' : (productToEdit ? 'Actualizar Producto' : 'Crear Producto')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* --- BULK FORM --- */
                        <div className="space-y-4">
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Código</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Marca *</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Color</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Talla</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-purple-600 uppercase tracking-wider w-28">Costo (USD)</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Precio</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Cant.</th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Tipo</th>
                                            <th className="px-3 py-3 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {bulkRows.map((row) => (
                                            <tr key={row.id}>
                                                <td className="px-3 py-2">
                                                    <CustomCombobox
                                                        value={row.cod_producto}
                                                        onChange={(val) => {
                                                            const found = codeBrands.find(cb => cb.code.toLowerCase() === val.toLowerCase());
                                                            // We update directly to avoid race conditions with multiple updateRow calls
                                                            setBulkRows(prev => prev.map(r => {
                                                                if (r.id === row.id) {
                                                                    return {
                                                                        ...r,
                                                                        cod_producto: val,
                                                                        marca: found ? found.brand : r.marca
                                                                    };
                                                                }
                                                                return r;
                                                            }));
                                                        }}
                                                        options={existingCodes}
                                                        placeholder="REQ..."
                                                        allowCustom={true}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CustomCombobox
                                                        value={row.marca}
                                                        onChange={(val) => updateRow(row.id, 'marca', val)}
                                                        options={existingBrands}
                                                        placeholder="Marca..."
                                                        allowCustom={false} // Strict
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CustomCombobox
                                                        value={row.color}
                                                        onChange={(val) => updateRow(row.id, 'color', val)}
                                                        options={getColorsForBrand(row.marca)}
                                                        placeholder="Color"
                                                        allowCustom={true}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CustomCombobox
                                                        value={row.talla}
                                                        onChange={(val) => updateRow(row.id, 'talla', val)}
                                                        options={getSizesForBrand(row.marca)}
                                                        placeholder="Talla"
                                                        allowCustom={true}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1.5 text-purple-700 text-sm font-bold">$</span>
                                                        <input
                                                            type="number"
                                                            value={row.costo || ''}
                                                            onChange={(e) => updateRow(row.id, 'costo', e.target.value ? parseFloat(e.target.value) : undefined)}
                                                            className="w-full pl-5 px-2 py-1 border border-purple-200 bg-purple-50 rounded text-sm focus:ring-1 focus:ring-purple-500"
                                                            placeholder="Op..."
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1.5 text-gray-500 text-sm">$</span>
                                                        <input
                                                            type="number"
                                                            value={row.precio}
                                                            onChange={(e) => updateRow(row.id, 'precio', parseFloat(e.target.value))}
                                                            className="w-full pl-5 px-2 py-1 border border-gray-300 rounded text-sm"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        value={row.stock}
                                                        onChange={(e) => updateRow(row.id, 'stock', parseInt(e.target.value))}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-center text-sm"
                                                        min="1"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CustomCombobox
                                                        value={(row.type || initialInventoryType) === 'novias' ? 'Novias' : 'General'}
                                                        onChange={(val) => updateRow(row.id, 'type', val === 'Novias' ? 'novias' : 'maestro')}
                                                        options={['General', 'Novias']}
                                                        placeholder="Tipo"
                                                        allowCustom={false}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {bulkRows.length > 1 && (
                                                        <button onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700">
                                                            <TrashIcon className="w-5 h-5" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button
                                onClick={addRow}
                                className="flex items-center space-x-2 text-blue-600 font-medium hover:text-blue-800"
                            >
                                <PlusIcon className="w-5 h-5" />
                                <span>Agregar Nueva Fila</span>
                            </button>

                            <div className="flex justify-end pt-6 border-t mt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 mr-3"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleBulkSubmit}
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                                >
                                    {loading ? 'Procesando...' : 'Guardar Todos'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};
